import * as React from "react"
import {
  type SearchEntitiesQueryResult,
  type SearchResultFieldsFragment,
} from "@digits-graphql/frontend/graphql-bearer"
import { LogoLoading } from "@digits-shared/components/Loaders"
import { SvgChevronDown } from "@digits-shared/components/SVGIcons/line/ChevronDown.svg"
import ScrollListener from "@digits-shared/components/UI/Elements/ScrollListener"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import styled from "styled-components"

const PAGE_SIZE = 200

/*
 STYLES
*/

const FetchAction = styled.div`
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const LoadingMore = styled.div`
  margin: 0 25px 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`

/*
 INTERFACES
*/

interface FetchMoreProps extends Pick<SearchEntitiesQueryResult, "fetchMore"> {
  search: SearchResultFieldsFragment
}

/*
 COMPONENT
*/

export const FetchMoreResults: React.FC<FetchMoreProps> = ({ search, fetchMore }) => {
  const { total, results } = search
  const [loading, setLoading] = React.useState(false)

  const offset = results.length
  const limit = Math.min(PAGE_SIZE, total - offset)

  const onFetchMoreClick = React.useCallback(() => {
    setLoading(true)
    fetchMore({
      variables: { limit, offset },
    }).finally(() => setLoading(false))
  }, [fetchMore, offset, limit])

  const onLoadMore = React.useCallback(() => {
    if (loading) return
    if (limit <= 0) {
      return false
    }
    onFetchMoreClick()
    return undefined
  }, [limit, loading, onFetchMoreClick])

  if (limit <= 0) {
    return null
  }

  if (loading) {
    return (
      <LoadingMore>
        <LogoLoading size="40px" />
      </LoadingMore>
    )
  }

  // first page
  if (offset < PAGE_SIZE) {
    return (
      <FetchAction>
        <DigitsButton $variant="secondary-dark" onClick={onFetchMoreClick}>
          <SvgChevronDown />
          See {total - offset} more
        </DigitsButton>
      </FetchAction>
    )
  }

  return <ScrollListener loadMore={onLoadMore} />
}
